import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { banRequestsApi } from "../api/services/ban/banService";
import {
  BanRequestCreate,
  BanRequestUpdate,
  BanRequestApproval,
  GetBanRequestsParams,
} from "../api/services/ban/type";

const BAN_REQUESTS_STALE_TIME =
  Number(process.env.REACT_APP_BAN_REQUESTS_STALE_TIME) || 5 * 60 * 1000;

export const banRequestKeys = {
  all: ["banRequests"] as const,
  lists: () => [...banRequestKeys.all, "list"] as const,
  list: (params: GetBanRequestsParams) =>
    [...banRequestKeys.lists(), params] as const,
  details: () => [...banRequestKeys.all, "detail"] as const,
  detail: (id: string) => [...banRequestKeys.details(), id] as const,
  history: (customerId: string) =>
    [...banRequestKeys.all, "history", customerId] as const,
};

export const useBanRequests = (params?: GetBanRequestsParams) => {
  return useQuery({
    queryKey: banRequestKeys.list(params || {}),
    queryFn: () => banRequestsApi.listBanRequests(params),
    staleTime: BAN_REQUESTS_STALE_TIME,
    enabled: !!params,
  });
};

export const useBanRequest = (banRequestId: string) => {
  return useQuery({
    queryKey: banRequestKeys.detail(banRequestId),
    queryFn: () => banRequestsApi.getBanRequest(banRequestId),
    enabled: !!banRequestId,
    staleTime: BAN_REQUESTS_STALE_TIME,
  });
};

export const useCustomerBanHistory = (customerId: string) => {
  return useQuery({
    queryKey: banRequestKeys.history(customerId),
    queryFn: () => banRequestsApi.getCustomerBanHistory(customerId),
    enabled: !!customerId,
    staleTime: BAN_REQUESTS_STALE_TIME,
  });
};

export const useCreateBanRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BanRequestCreate) =>
      banRequestsApi.createBanRequest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: banRequestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};

export const useUpdateBanRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      banRequestId,
      data,
    }: {
      banRequestId: string;
      data: BanRequestUpdate;
    }) => banRequestsApi.updateBanRequest(banRequestId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: banRequestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: banRequestKeys.details() });
    },
  });
};

export const useApproveBanRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (banRequestId: string) =>
      banRequestsApi.approveBanRequest(banRequestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: banRequestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};

export const useRejectBanRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      banRequestId,
      data,
    }: {
      banRequestId: string;
      data: BanRequestApproval;
    }) => banRequestsApi.rejectBanRequest(banRequestId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: banRequestKeys.lists() });
    },
  });
};

export const useDeleteBanRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (banRequestId: string) =>
      banRequestsApi.deleteBanRequest(banRequestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: banRequestKeys.lists() });
    },
  });
};

export const useUnbanCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customerId: string) =>
      banRequestsApi.unbanCustomer(customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: banRequestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};
